/**
 * Sky Map Visualization
 * Real-time interactive dome view showing sun and moon positions
 * Synced to user's device time with smooth animation
 */

import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SkyMap({ celestialData: propData = null, compact = false }) {
  const [baseData, setBaseData] = useState(propData);
  const [loading, setLoading] = useState(!propData);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showStars, setShowStars] = useState(true);
  // GEOGRAPHIC ANCHOR: North American Continent - Mississippi River Valley (St. Louis/Cahokia)
  // Fallback coordinates: ~38.6270° N, 90.1994° W (St. Louis, MO - center of the Promised Land)
  const [location, setLocation] = useState({ lat: 38.6270, lon: -90.1994 });
  const [locationName, setLocationName] = useState("St. Louis/Cahokia (Default)");
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Real-time calculated positions
  const [realtimeData, setRealtimeData] = useState(null);

  // Calculate sun position for given time and location
  const calculateSunPosition = useCallback((date, lat, lon) => {
    // Julian Day calculation
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const hour = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    
    let jd;
    if (month <= 2) {
      jd = Math.floor(365.25 * (year - 1 + 4716)) + Math.floor(30.6001 * (month + 13)) + day + hour / 24 - 1524.5;
    } else {
      jd = Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + hour / 24 - 1524.5;
    }
    
    // Days since J2000.0
    const n = jd - 2451545.0;
    
    // Mean longitude and anomaly
    const L = (280.460 + 0.9856474 * n) % 360;
    const g = ((357.528 + 0.9856003 * n) % 360) * Math.PI / 180;
    
    // Ecliptic longitude
    const lambda = L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g);
    
    // Obliquity of ecliptic
    const epsilon = 23.439 - 0.0000004 * n;
    const epsilonRad = epsilon * Math.PI / 180;
    
    // Declination
    const declination = Math.asin(Math.sin(epsilonRad) * Math.sin(lambda * Math.PI / 180)) * 180 / Math.PI;
    
    // Right ascension
    const ra = Math.atan2(
      Math.cos(epsilonRad) * Math.sin(lambda * Math.PI / 180),
      Math.cos(lambda * Math.PI / 180)
    ) * 180 / Math.PI;
    
    // Greenwich Mean Sidereal Time
    const gmst = (280.46061837 + 360.98564736629 * (jd - 2451545.0)) % 360;
    
    // Local Sidereal Time
    const lst = (gmst + lon) % 360;
    
    // Hour angle
    const ha = (lst - ra) * Math.PI / 180;
    
    // Convert to altitude and azimuth
    const latRad = lat * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    
    const altitude = Math.asin(
      Math.sin(latRad) * Math.sin(decRad) +
      Math.cos(latRad) * Math.cos(decRad) * Math.cos(ha)
    ) * 180 / Math.PI;
    
    let azimuth = Math.atan2(
      -Math.sin(ha),
      Math.tan(decRad) * Math.cos(latRad) - Math.sin(latRad) * Math.cos(ha)
    ) * 180 / Math.PI;
    azimuth = (azimuth + 360) % 360;
    
    return { altitude, azimuth, declination };
  }, []);

  // Calculate moon position and phase for given time
  const calculateMoonData = useCallback((date) => {
    const synodic = 29.530588853;
    const refNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
    const daysSince = (date.getTime() - refNewMoon.getTime()) / (1000 * 60 * 60 * 24);
    const ageDays = daysSince % synodic;
    const phase = ageDays / synodic;
    const illumination = 50 * (1 - Math.cos(2 * Math.PI * phase));
    
    let phaseName;
    if (ageDays < 1.85) phaseName = "New Moon";
    else if (ageDays < 7.38) phaseName = "Waxing Crescent";
    else if (ageDays < 9.23) phaseName = "First Quarter";
    else if (ageDays < 14.77) phaseName = "Waxing Gibbous";
    else if (ageDays < 16.61) phaseName = "Full Moon";
    else if (ageDays < 22.15) phaseName = "Waning Gibbous";
    else if (ageDays < 24.00) phaseName = "Last Quarter";
    else phaseName = "Waning Crescent";
    
    const isWaxing = ageDays < synodic / 2;
    
    return { phase, phaseName, illumination, ageDays, isWaxing };
  }, []);

  // Update real-time data every second
  const updateRealtimeData = useCallback(() => {
    const now = new Date();
    setCurrentTime(now);
    
    const sunPos = calculateSunPosition(now, location.lat, location.lon);
    const moonData = calculateMoonData(now);
    
    // Calculate moon azimuth (simplified - offset from sun by phase)
    const moonAzimuth = (sunPos.azimuth + 180 + moonData.ageDays * 12.2) % 360;
    // Moon altitude varies with phase
    const moonAltitude = 45 * Math.sin(2 * Math.PI * (moonData.phase - 0.25));
    
    setRealtimeData({
      sun: {
        altitude: sunPos.altitude,
        azimuth: sunPos.azimuth,
        declination: sunPos.declination
      },
      moon: {
        ...moonData,
        altitude: moonAltitude,
        azimuth: moonAzimuth
      },
      timestamp: now.toISOString()
    });
  }, [location, calculateSunPosition, calculateMoonData]);

  // Get user's location on mount
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude
          });
          setLocationName("Your Location");
        },
        () => {
          // Keep default St. Louis/Cahokia location
          setLocationName("St. Louis/Cahokia (Default)");
        },
        { timeout: 10000, enableHighAccuracy: true, maximumAge: 300000 }
      );
    }
  }, []);

  // Fetch base celestial data for additional info (gates, feasts, etc.)
  const fetchBaseData = useCallback(async () => {
    if (propData) {
      setBaseData(propData);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/celestial/data`, {
        params: { latitude: location.lat, longitude: location.lon },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBaseData(response.data);
    } catch (err) {
      console.error("Failed to fetch celestial data:", err);
    } finally {
      setLoading(false);
    }
  }, [propData, location]);

  // Initial data fetch and periodic refresh for base data
  useEffect(() => {
    fetchBaseData();
    // Refresh base data every 5 minutes
    const dataInterval = setInterval(fetchBaseData, 5 * 60 * 1000);
    return () => clearInterval(dataInterval);
  }, [fetchBaseData]);

  // Real-time animation loop
  useEffect(() => {
    // Start real-time updates
    updateRealtimeData();
    
    // Update every second for smooth movement
    const interval = setInterval(updateRealtimeData, 1000);
    
    return () => {
      clearInterval(interval);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [updateRealtimeData]);

  // Redraw canvas when data changes
  useEffect(() => {
    if (realtimeData && canvasRef.current) {
      drawSkyMap();
    }
  }, [realtimeData, showStars]);

  // Convert altitude/azimuth to canvas coordinates
  const toCanvasCoords = (altitude, azimuth, centerX, centerY, radius) => {
    // Altitude 0° = edge, 90° = center
    const r = radius * (1 - altitude / 90);
    // Azimuth: 0° = North, 90° = East, 180° = South, 270° = West
    const angle = (azimuth - 90) * (Math.PI / 180); // Adjust so 0° is top
    const x = centerX + r * Math.cos(angle);
    const y = centerY + r * Math.sin(angle);
    return { x, y };
  };

  // Draw the sky map
  const drawSkyMap = () => {
    const canvas = canvasRef.current;
    if (!canvas || !realtimeData) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 30;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Determine if it's day or night based on sun altitude
    const sunAltitude = realtimeData.sun?.altitude || 0;
    const isDay = sunAltitude > 0;
    const isDusk = sunAltitude > -18 && sunAltitude <= 0;

    // Draw sky background gradient
    let skyGradient;
    if (isDay) {
      skyGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      skyGradient.addColorStop(0, "#87CEEB"); // Light blue at zenith
      skyGradient.addColorStop(0.7, "#4A90D9"); // Deeper blue
      skyGradient.addColorStop(1, "#1E3A5F"); // Dark blue at horizon
    } else if (isDusk) {
      skyGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      skyGradient.addColorStop(0, "#2C3E50"); // Dark blue
      skyGradient.addColorStop(0.5, "#8E44AD"); // Purple
      skyGradient.addColorStop(1, "#E74C3C"); // Red/orange at horizon
    } else {
      skyGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      skyGradient.addColorStop(0, "#0a0a1a"); // Very dark blue at zenith
      skyGradient.addColorStop(1, "#1a1a2e"); // Slightly lighter at horizon
    }

    // Draw sky dome
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = skyGradient;
    ctx.fill();

    // Draw stars at night
    if (!isDay && showStars) {
      drawStars(ctx, centerX, centerY, radius);
    }

    // Draw altitude circles (30°, 60°)
    ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
    ctx.setLineDash([5, 5]);
    [30, 60].forEach((alt) => {
      const r = radius * (1 - alt / 90);
      ctx.beginPath();
      ctx.arc(centerX, centerY, r, 0, 2 * Math.PI);
      ctx.stroke();
      
      // Label
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.font = "10px Arial";
      ctx.fillText(`${alt}°`, centerX + r + 5, centerY);
    });
    ctx.setLineDash([]);

    // Draw horizon line
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = "#FFD700";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw cardinal directions
    const directions = [
      { label: "N", angle: -90 },
      { label: "E", angle: 0 },
      { label: "S", angle: 90 },
      { label: "W", angle: 180 },
    ];

    directions.forEach((dir) => {
      const angleRad = dir.angle * (Math.PI / 180);
      const x = centerX + (radius + 15) * Math.cos(angleRad);
      const y = centerY + (radius + 15) * Math.sin(angleRad);
      
      ctx.fillStyle = "#FFD700";
      ctx.font = "bold 14px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(dir.label, x, y);
    });

    // Draw intercardinal directions
    const interCardinals = [
      { label: "NE", angle: -45 },
      { label: "SE", angle: 45 },
      { label: "SW", angle: 135 },
      { label: "NW", angle: -135 },
    ];

    interCardinals.forEach((dir) => {
      const angleRad = dir.angle * (Math.PI / 180);
      const x = centerX + (radius + 12) * Math.cos(angleRad);
      const y = centerY + (radius + 12) * Math.sin(angleRad);
      
      ctx.fillStyle = "#888";
      ctx.font = "10px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(dir.label, x, y);
    });

    // Draw Sun
    if (realtimeData.sun) {
      const sunPos = toCanvasCoords(
        Math.max(realtimeData.sun.altitude, -10), // Show sun slightly below horizon
        realtimeData.sun.azimuth,
        centerX,
        centerY,
        radius
      );

      // Sun glow
      const sunGlow = ctx.createRadialGradient(sunPos.x, sunPos.y, 0, sunPos.x, sunPos.y, 30);
      sunGlow.addColorStop(0, "rgba(255, 200, 0, 0.8)");
      sunGlow.addColorStop(0.5, "rgba(255, 150, 0, 0.3)");
      sunGlow.addColorStop(1, "rgba(255, 100, 0, 0)");
      
      ctx.beginPath();
      ctx.arc(sunPos.x, sunPos.y, 30, 0, 2 * Math.PI);
      ctx.fillStyle = sunGlow;
      ctx.fill();

      // Sun body
      ctx.beginPath();
      ctx.arc(sunPos.x, sunPos.y, 12, 0, 2 * Math.PI);
      ctx.fillStyle = realtimeData.sun.altitude > 0 ? "#FFD700" : "#FFA500";
      ctx.fill();
      ctx.strokeStyle = "#FF8C00";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Sun label
      ctx.fillStyle = "#fff";
      ctx.font = "10px Arial";
      ctx.textAlign = "center";
      ctx.fillText("☀️", sunPos.x, sunPos.y - 20);
      ctx.fillText(`${realtimeData.sun.altitude.toFixed(1)}°`, sunPos.x, sunPos.y + 25);
    }

    // Draw Moon
    if (realtimeData.moon) {
      const moonPos = toCanvasCoords(
        Math.max(realtimeData.moon.altitude, -10),
        realtimeData.moon.azimuth,
        centerX,
        centerY,
        radius
      );

      // Moon glow
      const moonGlow = ctx.createRadialGradient(moonPos.x, moonPos.y, 0, moonPos.x, moonPos.y, 25);
      moonGlow.addColorStop(0, "rgba(200, 200, 255, 0.5)");
      moonGlow.addColorStop(1, "rgba(150, 150, 200, 0)");
      
      ctx.beginPath();
      ctx.arc(moonPos.x, moonPos.y, 25, 0, 2 * Math.PI);
      ctx.fillStyle = moonGlow;
      ctx.fill();

      // Moon body with phase
      drawMoonPhase(ctx, moonPos.x, moonPos.y, 10, realtimeData.moon.illumination, realtimeData.moon.isWaxing);

      // Moon label
      ctx.fillStyle = "#fff";
      ctx.font = "10px Arial";
      ctx.textAlign = "center";
      ctx.fillText(`${realtimeData.moon.illumination.toFixed(0)}%`, moonPos.x, moonPos.y + 22);
    }

    // Draw zenith marker
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    ctx.font = "9px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Zenith", centerX, centerY - 10);
  };

  // Draw stars for night sky
  const drawStars = (ctx, centerX, centerY, radius) => {
    // Seed random for consistent stars
    const pseudoRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    for (let i = 0; i < 100; i++) {
      const angle = pseudoRandom(i * 1.1) * 2 * Math.PI;
      const r = pseudoRandom(i * 2.2) * radius * 0.95;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      const size = pseudoRandom(i * 3.3) * 1.5 + 0.5;
      const brightness = pseudoRandom(i * 4.4) * 0.5 + 0.5;

      ctx.beginPath();
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(255, 255, 255, ${brightness})`;
      ctx.fill();
    }
  };

  // Draw moon with proper phase
  const drawMoonPhase = (ctx, x, y, radius, illumination, isWaxing) => {
    // Draw the lit portion of the moon
    ctx.save();
    
    // Base moon (dark)
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = "#333";
    ctx.fill();
    
    // Calculate phase
    const phase = illumination / 100;
    
    // Draw illuminated portion
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.clip();
    
    if (phase < 0.5) {
      // Crescent
      const offset = radius * (1 - phase * 2);
      ctx.beginPath();
      ctx.arc(isWaxing ? x + offset : x - offset, y, radius * 1.5, 0, 2 * Math.PI);
      ctx.fillStyle = "#333";
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "#E8E8E8";
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(isWaxing ? x - offset : x + offset, y, radius * 1.5, 0, 2 * Math.PI);
      ctx.fillStyle = "#333";
      ctx.fill();
    } else {
      // Gibbous
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "#E8E8E8";
      ctx.fill();
      
      const offset = radius * ((phase - 0.5) * 2);
      if (phase < 1) {
        ctx.beginPath();
        ctx.arc(isWaxing ? x + offset : x - offset, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = "#333";
        ctx.fill();
      }
    }
    
    ctx.restore();
    
    // Moon border
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = "#87CEEB";
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "30px" }}>
        <div style={{ fontSize: "2rem" }}>🌌</div>
        <p style={{ color: "#888" }}>Loading Sky Map...</p>
      </div>
    );
  }

  const canvasSize = compact ? 250 : 400;

  return (
    <div
      data-testid="sky-map"
      style={{
        background: "linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)",
        borderRadius: "15px",
        padding: compact ? "15px" : "20px",
        border: "1px solid #333",
      }}
    >
      {/* Header */}
      {!compact && (
        <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ color: "#87CEEB", margin: "0 0 5px" }}>
              🌌 Real-Time Sky Map
            </h3>
            <p style={{ color: "#888", margin: 0, fontSize: "0.8rem" }}>
              📍 {locationName} • Live tracking
            </p>
          </div>
          <div style={{
            background: "#1a3a1a",
            border: "1px solid #90EE90",
            borderRadius: "20px",
            padding: "4px 12px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            <span style={{
              width: "8px",
              height: "8px",
              background: "#90EE90",
              borderRadius: "50%",
              animation: "pulse 1s infinite"
            }}></span>
            <span style={{ color: "#90EE90", fontSize: "0.75rem" }}>LIVE</span>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          style={{
            borderRadius: "50%",
            boxShadow: "0 0 30px rgba(135, 206, 235, 0.2)",
          }}
        />
      </div>

      {/* Legend & Controls */}
      <div
        style={{
          marginTop: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "15px", fontSize: "0.8rem" }}>
          <span style={{ color: "#FFD700" }}>☀️ Sun: {realtimeData?.sun?.altitude?.toFixed(1)}°</span>
          <span style={{ color: "#87CEEB" }}>
            🌙 Moon: {realtimeData?.moon?.illumination?.toFixed(0)}%
          </span>
        </div>
        
        {!compact && (
          <label style={{ color: "#888", fontSize: "0.8rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showStars}
              onChange={(e) => setShowStars(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Show Stars
          </label>
        )}
      </div>

      {/* Position Details */}
      {!compact && realtimeData && (
        <div
          style={{
            marginTop: "15px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
          }}
        >
          <div
            style={{
              background: "#111",
              borderRadius: "8px",
              padding: "10px",
              border: "1px solid #FFD70040",
            }}
          >
            <div style={{ color: "#FFD700", fontWeight: "bold", marginBottom: "5px" }}>
              ☀️ Sun Position
            </div>
            <div style={{ color: "#aaa", fontSize: "0.8rem" }}>
              <div>Altitude: {realtimeData.sun?.altitude?.toFixed(1)}°</div>
              <div>Azimuth: {realtimeData.sun?.azimuth?.toFixed(1)}°</div>
              <div>
                Status: {realtimeData.sun?.altitude > 0 ? "Above Horizon ☀️" : "Below Horizon 🌙"}
              </div>
            </div>
          </div>
          
          <div
            style={{
              background: "#111",
              borderRadius: "8px",
              padding: "10px",
              border: "1px solid #87CEEB40",
            }}
          >
            <div style={{ color: "#87CEEB", fontWeight: "bold", marginBottom: "5px" }}>
              🌙 Moon Position
            </div>
            <div style={{ color: "#aaa", fontSize: "0.8rem" }}>
              <div>Phase: {realtimeData.moon?.phaseName}</div>
              <div>Illumination: {realtimeData.moon?.illumination?.toFixed(1)}%</div>
              <div>Age: {realtimeData.moon?.ageDays?.toFixed(1)} days</div>
            </div>
          </div>
        </div>
      )}

      {/* Time Display */}
      {!compact && (
        <div
          style={{
            marginTop: "10px",
            textAlign: "center",
            color: "#666",
            fontSize: "0.75rem",
          }}
        >
          <span style={{ color: "#90EE90" }}>⏱️ {currentTime.toLocaleTimeString()}</span>
          <span style={{ marginLeft: "10px" }}>
            Coordinates: {location.lat.toFixed(4)}°, {location.lon.toFixed(4)}°
          </span>
        </div>
      )}
      
      {/* Pulse animation for LIVE indicator */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default SkyMap;
